import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  QrCodeIcon,
  PlayIcon,
  StopIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  ChatBubbleBottomCenterTextIcon,
  CheckIcon,
  SparklesIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

import { api } from '../services/api';
import toast from '../utils/toast';

// QR Code Modal Component
// QR Code Modal Component - Mobile Responsive Version with Pairing Code Support
const QRCodeModal = ({ isOpen, qrCode, onClose, sessionInfo, refreshing, client }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [authMode, setAuthMode] = useState('qr'); // 'qr' or 'pairing'
  const [pairingCode, setPairingCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loadingPairingCode, setLoadingPairingCode] = useState(false);
  const [pairingError, setPairingError] = useState('');

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  if (!isOpen && !isClosing) return null;

  return (
    <div className={`fixed inset-0 z-50 overflow-y-auto transition-opacity duration-300 ${
      isOpen && !isClosing ? 'opacity-100' : 'opacity-0'
    }`}>
      <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
        {/* Backdrop */}
        <div
          className={`fixed inset-0 bg-black transition-opacity duration-300 ${
            isOpen && !isClosing ? 'bg-opacity-50' : 'bg-opacity-0'
          }`}
          onClick={handleClose}
        />

        {/* Modal - Responsive sizing */}
        <div className={`relative bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-sm sm:max-w-md lg:max-w-lg mx-2 sm:mx-4 transform transition-all duration-300 ${
          isOpen && !isClosing
            ? 'scale-100 translate-y-0 opacity-100'
            : 'scale-95 translate-y-4 opacity-0'
        }`}>
          {/* Header - Mobile optimized */}
          <div className="flex items-center justify-between p-4 sm:p-6 pb-3 sm:pb-4">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center">
                <QrCodeIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Connect WhatsApp</h3>
                <p className="text-xs sm:text-sm text-gray-500">Choose your connection method</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <XMarkIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
            </button>
          </div>

          {/* Authentication Method Toggle */}
          <div className="px-4 sm:px-6 mb-4">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setAuthMode('qr')}
                className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
                  authMode === 'qr'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <QrCodeIcon className="h-4 w-4 inline mr-1" />
                QR Code
              </button>
              <button
                onClick={() => setAuthMode('pairing')}
                className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
                  authMode === 'pairing'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="inline-block w-4 h-4 mr-1 text-center">📱</span>
                Pairing Code
              </button>
            </div>
          </div>

          {/* Content Area - QR Code or Pairing Code */}
          <div className="px-4 sm:px-6 pb-4 sm:pb-6">
            <div className="bg-white border-2 border-gray-100 rounded-lg sm:rounded-xl p-3 sm:p-6 text-center">
              {authMode === 'qr' ? (
                // QR Code Mode
                qrCode ? (
                  <div className="space-y-3 sm:space-y-4">
                    <div className="w-full flex justify-center">
                      <img
                        src={qrCode}
                        alt="WhatsApp QR Code"
                        className="w-full h-auto max-w-[280px] sm:max-w-sm rounded-lg shadow-sm"
                        style={{
                          minHeight: '280px',
                          maxHeight: '320px',
                          objectFit: 'contain',
                          aspectRatio: '1'
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-center space-x-2 text-blue-600">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                        <span className="text-xs sm:text-sm font-medium">Waiting for scan...</span>
                        {refreshing && (
                          <ArrowPathIcon className="h-3 w-3 sm:h-4 sm:w-4 animate-spin" />
                        )}
                      </div>
                      {sessionInfo && sessionInfo.status && (
                        <p className="text-xs text-gray-500">
                          Status: <span className="font-mono text-blue-600">{sessionInfo.status}</span>
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="py-8 sm:py-12">
                    <ArrowPathIcon className="h-6 w-6 sm:h-8 sm:w-8 animate-spin text-gray-400 mx-auto mb-3 sm:mb-4" />
                    <p className="text-sm sm:text-base text-gray-500">Loading QR code...</p>
                  </div>
                )
              ) : (
                // Pairing Code Mode
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl">📱</span>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Enter Pairing Code</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      If QR code scanning doesn't work, use this alternative method
                    </p>
                  </div>

                  {/* Phone Number Input */}
                  <div className="space-y-3">
                    <div>
                      <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                        Your WhatsApp Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phoneNumber"
                        placeholder="e.g., 233201234567 or 0201234567"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                      {phoneNumber && (
                        <div className="mt-1 text-xs text-gray-500">
                          Will be formatted as: {
                            (() => {
                              let formatted = phoneNumber.trim().replace(/^\+/, '');
                              if (!formatted.startsWith('233')) {
                                if (formatted.startsWith('0')) {
                                  formatted = '233' + formatted.substring(1);
                                } else {
                                  formatted = '233' + formatted;
                                }
                              }
                              return formatted;
                            })()
                          }
                        </div>
                      )}
                    </div>

                    <button
                      onClick={async () => {
                        if (!phoneNumber.trim()) {
                          setPairingError('Please enter your phone number');
                          return;
                        }
                        
                        setLoadingPairingCode(true);
                        setPairingError('');
                        
                        try {
                          // Format phone number: remove +, add 233 if not present
                          let formattedPhone = phoneNumber.trim().replace(/^\+/, ''); // Remove leading +
                          if (!formattedPhone.startsWith('233')) {
                            // If starts with 0, replace with 233, otherwise prepend 233
                            if (formattedPhone.startsWith('0')) {
                              formattedPhone = '233' + formattedPhone.substring(1);
                            } else {
                              formattedPhone = '233' + formattedPhone;
                            }
                          }
                          
                          // Call API to request pairing code
                          const response = await api.requestPairingCode(client.port, client.client_id, client.webhook_api_key, formattedPhone);
                          setPairingCode(response.data.code);
                        } catch (error) {
                          console.error('Failed to request pairing code:', error);
                          setPairingError(error.response?.data?.error || 'Failed to request pairing code. Try QR code instead.');
                        } finally {
                          setLoadingPairingCode(false);
                        }
                      }}
                      disabled={loadingPairingCode || !phoneNumber.trim()}
                      className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                    >
                      {loadingPairingCode ? (
                        <>
                          <ArrowPathIcon className="h-4 w-4 animate-spin inline mr-2" />
                          Requesting Code...
                        </>
                      ) : (
                        'Get Pairing Code'
                      )}
                    </button>
                  </div>

                  {/* Pairing Code Display */}
                  {pairingCode && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="text-center">
                        <h5 className="font-semibold text-green-800 mb-2">Your Pairing Code:</h5>
                        <div className="text-2xl font-mono font-bold text-green-900 bg-white px-4 py-2 rounded border-2 border-green-300 inline-block">
                          {pairingCode}
                        </div>
                        <p className="text-sm text-green-700 mt-2">
                          Enter this code in WhatsApp: Settings → Linked Devices → Link a Device → Link with Phone Number
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Error Display */}
                  {pairingError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-800">{pairingError}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Instructions - Mobile optimized */}
            <div className="mt-4 sm:mt-6 space-y-2 sm:space-y-3">
              {authMode === 'qr' ? (
                // QR Code Instructions
                <>
                  <div className="flex items-start space-x-2 sm:space-x-3 text-xs sm:text-sm">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-blue-600">1</span>
                    </div>
                    <p className="text-gray-600">Open WhatsApp on your phone</p>
                  </div>
                  <div className="flex items-start space-x-2 sm:space-x-3 text-xs sm:text-sm">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-blue-600">2</span>
                    </div>
                    <p className="text-gray-600">Tap <strong>Menu</strong> or <strong>Settings</strong> and select <strong>Linked Devices</strong></p>
                  </div>
                  <div className="flex items-start space-x-2 sm:space-x-3 text-xs sm:text-sm">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-blue-600">3</span>
                    </div>
                    <p className="text-gray-600">Point your phone at this screen to capture the code</p>
                  </div>
                </>
              ) : (
                // Pairing Code Instructions
                <>
                  <div className="flex items-start space-x-2 sm:space-x-3 text-xs sm:text-sm">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-blue-600">1</span>
                    </div>
                    <p className="text-gray-600">Open WhatsApp on your phone</p>
                  </div>
                  <div className="flex items-start space-x-2 sm:space-x-3 text-xs sm:text-sm">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-blue-600">2</span>
                    </div>
                    <p className="text-gray-600">Go to <strong>Settings</strong> → <strong>Linked Devices</strong> → <strong>Link a Device</strong></p>
                  </div>
                  <div className="flex items-start space-x-2 sm:space-x-3 text-xs sm:text-sm">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-blue-600">3</span>
                    </div>
                    <p className="text-gray-600">Select <strong>Link with Phone Number</strong> and enter the code above</p>
                  </div>
                </>
              )}
              
              {/* Fallback message */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
                <p className="text-xs text-yellow-800">
                  <strong>Having trouble?</strong> Try switching between QR code and pairing code methods. Pairing codes work better on some Android devices.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Success Animation Modal
const SuccessModal = ({ isOpen, onClose, sessionInfo }) => {
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        handleClose();
      }, 3000); // Auto close after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [isOpen, handleClose]);

  if (!isOpen && !isClosing) return null;

  return (
    <div className={`fixed inset-0 z-50 overflow-y-auto transition-opacity duration-300 ${
      isOpen && !isClosing ? 'opacity-100' : 'opacity-0'
    }`}>
      <div className="flex items-center justify-center min-h-screen p-4">
        {/* Backdrop */}
        <div
          className={`fixed inset-0 bg-black transition-opacity duration-300 ${
            isOpen && !isClosing ? 'bg-opacity-30' : 'bg-opacity-0'
          }`}
        />

        {/* Modal */}
        <div className={`relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all duration-500 ${
          isOpen && !isClosing
            ? 'scale-100 translate-y-0 opacity-100'
            : 'scale-95 translate-y-8 opacity-0'
        }`}>
          <div className="p-8 text-center">
            {/* Success Animation */}
            <div className={`w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 transform transition-all duration-700 ${
              isOpen ? 'scale-100 rotate-0' : 'scale-0 rotate-180'
            }`}>
              <CheckIcon className="h-10 w-10 text-green-500" />
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-2">Connected Successfully!</h3>
            <p className="text-gray-600 mb-4">
              Your WhatsApp Business is now connected and ready to receive messages.
            </p>

            {sessionInfo?.me?.id && (
              <div className="bg-green-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-green-800">
                  <strong>Phone:</strong> {sessionInfo.me.id.replace('@c.us', '')}
                </p>
                {sessionInfo.me.pushName && (
                  <p className="text-sm text-green-800">
                    <strong>Name:</strong> {sessionInfo.me.pushName}
                  </p>
                )}
              </div>
            )}

            <div className="flex items-center justify-center space-x-2 text-green-600">
              <SparklesIcon className="h-5 w-5" />
              <span className="text-sm font-medium">AI Assistant is now active</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// WhatsApp Connected Interface Component
const WhatsAppConnectedInterface = ({ sessionInfo, client }) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* WhatsApp Interface */}
      <div className="bg-gradient-to-b from-green-500 to-green-600 rounded-t-2xl px-4 py-3">
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
              <span className="text-green-600 font-bold text-lg">
                {sessionInfo?.me?.pushName?.[0] || client?.display_name?.[0] || 'W'}
              </span>
            </div>
            <div>
              <div className="font-semibold">
                {sessionInfo?.me?.pushName || client?.display_name || 'WhatsApp Business'}
              </div>
              <div className="text-sm text-green-100 flex items-center">
                <div className="w-2 h-2 bg-green-300 rounded-full mr-2"></div>
                Online
              </div>
            </div>
          </div>
          <div className="text-sm text-green-100">
            {formatTime(currentTime)}
          </div>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="bg-gray-50 px-4 py-6 space-y-4" style={{ minHeight: '400px' }}>
        {/* Welcome Message */}
        <div className="flex justify-start">
          <div className="bg-white rounded-2xl rounded-tl-md px-4 py-3 max-w-xs shadow-sm">
            <p className="text-gray-800 text-sm">
              🎉 WhatsApp Business is now connected and ready to receive messages!
            </p>
            <div className="text-xs text-gray-500 mt-1 flex items-center justify-end">
              <CheckIcon className="w-3 h-3 text-blue-500 mr-1" />
              <CheckIcon className="w-3 h-3 text-blue-500 mr-1 -ml-1" />
              {formatTime(currentTime)}
            </div>
          </div>
        </div>

        {/* AI Status Message */}
        <div className="flex justify-start">
          <div className="bg-white rounded-2xl rounded-tl-md px-4 py-3 max-w-xs shadow-sm">
            <div className="flex items-center space-x-2 mb-2">
              <SparklesIcon className="w-4 h-4 text-purple-500" />
              <span className="text-sm font-medium text-gray-800">AI Assistant Active</span>
            </div>
            <p className="text-gray-700 text-sm">
              Your AI assistant is monitoring this chat and ready to help customers automatically.
            </p>
            <div className="text-xs text-gray-500 mt-1 flex items-center justify-end">
              <CheckIcon className="w-3 h-3 text-blue-500 mr-1" />
              <CheckIcon className="w-3 h-3 text-blue-500 mr-1 -ml-1" />
              {formatTime(new Date(Date.now() - 5000))}
            </div>
          </div>
        </div>

        {/* Sample Customer Message */}
        <div className="flex justify-end">
          <div className="bg-green-500 text-white rounded-2xl rounded-tr-md px-4 py-3 max-w-xs">
            <p className="text-sm">Hello! I have a question about your services.</p>
            <div className="text-xs text-green-100 mt-1 flex items-center justify-end">
              <CheckIcon className="w-3 h-3 mr-1" />
              <CheckIcon className="w-3 h-3 mr-1 -ml-1" />
              {formatTime(new Date(Date.now() - 2000))}
            </div>
          </div>
        </div>

        {/* AI Response */}
        <div className="flex justify-start">
          <div className="bg-white rounded-2xl rounded-tl-md px-4 py-3 max-w-xs shadow-sm">
            <div className="flex items-center space-x-2 mb-2">
              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              <span className="text-xs text-purple-600 font-medium">AI Response</span>
            </div>
            <p className="text-gray-800 text-sm">
              Hi there! I'd be happy to help you with any questions about our services. What would you like to know?
            </p>
            <div className="text-xs text-gray-500 mt-1 flex items-center justify-end">
              <CheckIcon className="w-3 h-3 text-blue-500 mr-1" />
              <CheckIcon className="w-3 h-3 text-blue-500 mr-1 -ml-1" />
              {formatTime(new Date())}
            </div>
          </div>
        </div>
      </div>

      {/* Status Bar */}
      <div className="bg-white rounded-b-2xl px-4 py-3 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-gray-700">Connected</span>
            </div>
            <div className="flex items-center space-x-2">
              <SparklesIcon className="w-4 h-4 text-purple-500" />
              <span className="text-gray-700">AI Active</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ClientDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [wasShowingQR, setWasShowingQR] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const updateClientWhatsAppId = useCallback(async (whatsappId) => {
    if (!client?.client_id || !whatsappId) return;

    try {
      await api.updateClient(client.client_id, {
        whatsapp_id: whatsappId,
        display_name: sessionInfo?.me?.pushName || client?.display_name || 'WhatsApp Business'
      });

      // Update local client state
      setClient(prevClient => ({
        ...prevClient,
        whatsapp_id: whatsappId
      }));
    } catch (error) {
      // Don't show error to user since this is a background operation
    }
  }, [client?.client_id, client?.display_name, sessionInfo?.me?.pushName]);

  const fetchClientData = useCallback(async () => {
    try {
      setRefreshing(true);

      // Get client status
      const clientResponse = await api.getClientStatus(clientId);
      setClient(clientResponse.data);

      // If client has a port, try to get session info
      if (clientResponse.data.port) {
        try {
          const sessionResponse = await api.getWahaSessionInfo(
            clientResponse.data.port,
            clientId,
            clientResponse.data.webhook_api_key
          );
          setSessionInfo(sessionResponse.data);

          // Check if we just got authenticated (was showing QR, now status is WORKING)
          const isWorking = sessionResponse.data && sessionResponse.data.status === 'WORKING';
          if (wasShowingQR && isWorking) {
            setQrCode(null);
            setShowQRModal(false);
            setShowSuccessModal(true);
            toast.success('🎉 WhatsApp connected successfully!');
            setWasShowingQR(false);

            // NEW: Update WhatsApp ID in database if we have the phone number
            if (sessionResponse.data.me && sessionResponse.data.me.id) {
              await updateClientWhatsAppId(sessionResponse.data.me.id);
            }
          }

          // Try to get QR code if session exists but status is not WORKING
          if (sessionResponse.data && sessionResponse.data.status !== 'WORKING') {
            setWasShowingQR(true);
            try {
              const qrResponse = await api.getWahaQRCode(
                clientResponse.data.port,
                clientId,
                clientResponse.data.webhook_api_key
              );

              // Handle JSON response with base64 data
              if (qrResponse.data && qrResponse.data.data) {
                const base64Data = qrResponse.data.data;
                const mimeType = qrResponse.data.mimetype || 'image/jpeg';
                const qrUrl = `data:${mimeType};base64,${base64Data}`;
                setQrCode(qrUrl);

                // Show QR modal if not already showing and we have QR code
                if (!showQRModal && !isWorking) {
                  setShowQRModal(true);
                }
              }
            } catch (qrError) {
              console.warn('Could not fetch QR code:', qrError);
            }
          } else {
            // Clear QR code if status is WORKING
            if (isWorking) {
              setQrCode(null);
              setWasShowingQR(false);
              setShowQRModal(false);
            }
          }
        } catch (sessionError) {
          console.warn('Could not fetch session info:', sessionError);
          
          // If session doesn't exist (404), set a default stopped state
          if (sessionError.response && sessionError.response.status === 404) {
            setSessionInfo({
              status: 'STOPPED',
              authenticated: false,
              error: 'Session not found'
            });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching client data:', error);

      // Handle 404 - client not found (likely deleted)
      if (error.response && error.response.status === 404) {
        toast.error('Client not found or has been deleted');
        navigate('/dashboard/connections');
        return;
      }

      toast.error('Failed to load client data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [clientId, navigate, wasShowingQR, showQRModal, updateClientWhatsAppId]);

  useEffect(() => {
    fetchClientData();
    // More frequent refresh when QR code is showing (every 3 seconds)
    // Normal refresh when authenticated (every 10 seconds)
    const refreshInterval = qrCode ? 3000 : 10000;
    const interval = setInterval(fetchClientData, refreshInterval);
    return () => clearInterval(interval);
  }, [clientId, fetchClientData, qrCode]);

  const handleSessionAction = async (action) => {
    if (!client?.port) return;

    try {
      switch (action) {
        case 'create':
          await api.createClientSession(clientId, {
            webhook_api_key: client.webhook_api_key,
            email: client.email,
            whatsapp_id: client.whatsapp_id,
            agent_id: client.agent_id
          });
          toast.success('Session created and started');
          break;
        case 'start':
          // If agent status is stopped, enable auto-reply
          if (client.agent?.status === 'stopped' && client.agent_id) {
            await api.enableAgentAutoReply(client.agent_id);
            toast.success('Agent started');
          } else {
            // Reprovision recreates the container + WhatsApp session from scratch
            // (same as reprovision_prod.sh). This can take up to a few minutes and
            // requires re-scanning the QR code afterwards.
            if (!window.confirm(
              'Start will fully reprovision this connection: the container and ' +
              'WhatsApp session are recreated and you will need to re-scan the QR code. ' +
              'This can take a couple of minutes. Continue?'
            )) {
              return;
            }
            toast.loading('Reprovisioning connection...', { id: 'reprovision' });
            await api.reprovisionClient(clientId);
            toast.success('Connection reprovisioned. Re-scan the QR code to reconnect.', { id: 'reprovision' });
          }
          break;
        case 'stop':
          // await api.stopWahaSession(client.port, clientId, client.webhook_api_key);
          // Only disable auto-reply for this agent
          if (client.agent_id) {
            await api.disableAgentAutoReply(client.agent_id);
          }
          toast.success('Agent stopped');
          setShowQRModal(false); // Close QR modal if session stopped
          break;
        case 'restart':
          // Reprovision recreates the container + WhatsApp session from scratch
          // (same as reprovision_prod.sh). This can take up to a few minutes and
          // requires re-scanning the QR code afterwards.
          if (!window.confirm(
            'Restart will fully reprovision this connection: the container and ' +
            'WhatsApp session are recreated and you will need to re-scan the QR code. ' +
            'This can take a couple of minutes. Continue?'
          )) {
            return;
          }
          toast.loading('Reprovisioning connection...', { id: 'reprovision' });
          await api.reprovisionClient(clientId);
          toast.success('Connection reprovisioned. Re-scan the QR code to reconnect.', { id: 'reprovision' });
          break;
        case 'logout':
          await api.logoutWahaSession(client.port, clientId, client.webhook_api_key);
          toast.success('Session logged out');
          setShowQRModal(false); // Close QR modal if logged out
          break;
        default:
          return;
      }

      // Refresh data after action
      setTimeout(fetchClientData, 2000);
    } catch (error) {
      console.error(`Error ${action} session:`, error);
      if (action === 'restart' || action === 'start') {
        // Replace the in-progress reprovision loading toast with the error.
        toast.error('Failed to reprovision connection', { id: 'reprovision' });
      } else {
        toast.error(`Failed to ${action} session`);
      }
    }
  };

  const handleDeleteClient = async () => {
    if (!window.confirm(`Are you sure you want to delete ${clientId}?`)) {
      return;
    }

    try {
      await api.deleteClient(clientId);
      toast.success(`Connection ${clientId} deleted successfully`);
      navigate('/dashboard/connections');
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error('Failed to delete client');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
      case 'running':
        return <span className="status-running">Running</span>;
      case 'pending':
        return <span className="status-pending">Pending</span>;
      case 'stopped':
      case 'inactive':
        return <span className="status-stopped">Stopped</span>;
      case 'deleted':
        return <span className="status-error">Deleted</span>;
      default:
        return <span className="status-error">Unknown</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-whatsapp-500" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="h-16 w-16 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Agent not found</h3>
        <p className="text-gray-500 mb-4">The requested WhatsApp agent could not be found.</p>
        <Link to="/dashboard/connections" className="btn-primary">
          Back to Connections
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Back
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{clientId}</h1>
              <p className="text-gray-500 mt-1">WhatsApp Session Management</p>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={fetchClientData}
              className="btn-secondary btn-sm"
              disabled={refreshing}
            >
              <ArrowPathIcon className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleDeleteClient}
              className="btn-danger btn-sm"
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              Delete Agent
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Client Information */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Agent Information</h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1">{getStatusBadge(client.agent?.status || client.status)}</dd>
              </div>
              {/*<div>*/}
              {/*  <dt className="text-sm font-medium text-gray-500">Port</dt>*/}
              {/*  <dd className="mt-1 text-sm text-gray-900">{client.port || 'N/A'}</dd>*/}
              {/*</div>*/}
              <div>
                <dt className="text-sm font-medium text-gray-500">Process ID</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono">
                  {client.container_id || 'N/A'}
                </dd>
              </div>
              {/*{client.port && (*/}
              {/*  <div>*/}
              {/*    <dt className="text-sm font-medium text-gray-500">Instance Host</dt>*/}
              {/*    <dd className="mt-1">*/}
              {/*      <a*/}
              {/*        href={`http://localhost:${client.port}`}*/}
              {/*        target="_blank"*/}
              {/*        rel="noopener noreferrer"*/}
              {/*        className="text-whatsapp-600 hover:text-whatsapp-800 text-sm"*/}
              {/*      >*/}
              {/*        http://localhost:{client.port}*/}
              {/*      </a>*/}
              {/*    </dd>*/}
              {/*  </div>*/}
              {/*)}*/}
            </dl>
          </div>

          {/* Session Information */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">WhatsApp Session</h2>
            {sessionInfo ? (
              <div className="space-y-4">
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Session Status</dt>
                    <dd className="mt-1">
                      <span className={`status-badge ${
                        sessionInfo.status === 'WORKING' ? 'bg-green-100 text-green-800' :
                          sessionInfo.status === 'STARTING' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                      }`}>
                        {sessionInfo.status}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Authenticated</dt>
                    <dd className="mt-1">
                      <span className={`status-badge ${
                        sessionInfo.status === 'WORKING' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {sessionInfo.status === 'WORKING' ? 'Yes' : 'No'}
                      </span>
                    </dd>
                  </div>
                  {sessionInfo.me && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Phone Number</dt>
                      <dd className="mt-1 text-sm text-gray-900">{sessionInfo.me.id.replace('@c.us', '')}</dd>
                    </div>
                  )}
                </dl>

                {/* Session Actions */}
                <div className="flex space-x-2 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => handleSessionAction(sessionInfo.status === 'FAILED' ? 'restart' : sessionInfo.status === 'STOPPED' ? 'create' : client.agent?.status === 'stopped' ? 'start' : 'start')}
                    className={`btn-sm ${(sessionInfo.status === 'WORKING' && client.agent?.status !== 'stopped') ? 'btn-secondary opacity-50 cursor-not-allowed' : 'btn-primary'}`}
                    disabled={sessionInfo.status === 'WORKING' && client.agent?.status !== 'stopped'}
                    title={sessionInfo.status === 'FAILED' ? 'Recreate session with fresh QR code' : client.agent?.status === 'stopped' ? 'Start agent' : 'Start session'}
                  >
                    <PlayIcon className="h-4 w-4 mr-1" />
                    {client.agent?.status === 'stopped' ? 'Start' : sessionInfo.status === 'FAILED' ? 'Start' : 'Start'}
                  </button>
                  <button
                    onClick={() => handleSessionAction('stop')}
                    className="btn-secondary btn-sm"
                    title={'Stop this session'}
                  >
                    <StopIcon className="h-4 w-4 mr-1" />
                    Stop
                  </button>
                  <button
                    onClick={() => handleSessionAction(sessionInfo.status === 'STOPPED' ? 'create' : 'restart')}
                    className="btn-secondary btn-sm"
                    title={sessionInfo.status === 'FAILED' ? 'Recreate session with fresh QR code' : 'Restart current session'}
                  >
                    <ArrowPathIcon className="h-4 w-4 mr-1" />
                    {sessionInfo.status === 'FAILED' ? 'Restart' : 'Restart'}
                  </button>
                  {sessionInfo.authenticated && (
                    <button
                      onClick={() => handleSessionAction('logout')}
                      className="btn-danger btn-sm"
                    >
                      Logout
                    </button>
                  )}
                </div>

                {/* Manual QR Code trigger for testing */}
                {sessionInfo.status !== 'WORKING' && qrCode && (
                  <div className="pt-2">
                    <button
                      onClick={() => setShowQRModal(true)}
                      className="btn-secondary btn-sm w-full"
                    >
                      <QrCodeIcon className="h-4 w-4 mr-2" />
                      Show QR Code
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500">No session information available</p>
            )}
          </div>

          {/* WhatsApp Interface - Only show when WORKING */}
          {sessionInfo && sessionInfo.status === 'WORKING' && (
            <div className="card lg:col-span-2">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <ChatBubbleBottomCenterTextIcon className="h-5 w-5 mr-2 text-green-600" />
                WhatsApp Business Connected
                <div className="ml-2 flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-green-600 font-medium">Live</span>
                </div>
              </h2>
              <div>
                {/* Bento Grid Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Main Chat Interface - Spans 2 columns */}
                  <div className="lg:col-span-2">
                    <WhatsAppConnectedInterface sessionInfo={sessionInfo} client={client} />
                  </div>

                  {/* Side Stats */}
                  <div className="space-y-4">
                    {/* Connection Status */}
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200 p-4">
                      <div className="text-center">
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-2">
                          <CheckIcon className="w-5 h-5 text-white" />
                        </div>
                        <div className="text-sm font-semibold text-green-800">Connected</div>
                        <div className="text-xs text-green-600 mt-1">
                          Status: {sessionInfo.status}
                        </div>
                      </div>
                    </div>

                    {/* AI Assistant */}
                    <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl border border-purple-200 p-4">
                      <div className="text-center">
                        <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-2">
                          <SparklesIcon className="w-5 h-5 text-white" />
                        </div>
                        <div className="text-sm font-semibold text-purple-800">AI Active</div>
                        <div className="text-xs text-purple-600 mt-1">
                          Ready to assist
                        </div>
                      </div>
                    </div>

                    {/* Phone Number */}
                    <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-blue-200 p-4">
                      <div className="text-center">
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-2">
                          <ChatBubbleBottomCenterTextIcon className="w-5 h-5 text-white" />
                        </div>
                        <div className="text-sm font-semibold text-blue-800">Phone</div>
                        <div className="text-xs text-blue-600 mt-1 font-mono">
                          {sessionInfo?.me?.id?.replace('@c.us', '') || 'Loading...'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-center mt-6">
                  <p className="text-sm text-green-600 font-medium">
                    ✅ Your WhatsApp Business is connected and ready to receive messages
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    Messages will be processed by your AI assistant automatically
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Connection Status Card - Show when not WORKING */}
          {sessionInfo && sessionInfo.status !== 'WORKING' && (
            <div className="card lg:col-span-2">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <QrCodeIcon className="h-5 w-5 mr-2 text-blue-600" />
                WhatsApp Connection Required
                <div className="ml-2 flex items-center space-x-2">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-yellow-600 font-medium">Waiting</span>
                </div>
              </h2>
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <QrCodeIcon className="h-10 w-10 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Ready to Connect WhatsApp
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Your WhatsApp session is ready for authentication. Click the button below to scan the QR code with your WhatsApp mobile app.
                </p>
                <button
                  onClick={() => setShowQRModal(true)}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center mx-auto"
                  disabled={!qrCode}
                >
                  <QrCodeIcon className="h-5 w-5 mr-2" />
                  {qrCode ? 'Scan QR Code' : 'Loading QR Code...'}
                </button>
                {sessionInfo.status && (
                  <p className="text-xs text-gray-500 mt-4">
                    Current status: <span className="font-mono text-blue-600">{sessionInfo.status}</span>
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* QR Code Modal */}
      <QRCodeModal
        isOpen={showQRModal}
        qrCode={qrCode}
        onClose={() => setShowQRModal(false)}
        sessionInfo={sessionInfo}
        refreshing={refreshing}
        client={client}
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        sessionInfo={sessionInfo}
      />
    </>
  );
};

export default ClientDetail;